import { glMatrix, vec3 } from 'gl-matrix';

function HSVtoRGB(h: number, s: number, v: number): vec3 {
    let r: number, g: number, b: number;
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);
    switch (i % 6) {
        case 0:
            r = v;
            g = t;
            b = p;
            break;
        case 1:
            r = q;
            g = v;
            b = p;
            break;
        case 2:
            r = p;
            g = v;
            b = t;
            break;
        case 3:
            r = p;
            g = q;
            b = v;
            break;
        case 4:
            r = t;
            g = p;
            b = v;
            break;
        default:
            r = v;
            g = p;
            b = q;
            break;
    }
    return vec3.fromValues(r, g, b);
}

/**
 * Function to generate random color that is not gray scale or too dark
 */
function randomColor(): vec3 {
    const hue = Math.random();
    const saturation = 0.5 + 0.5 * Math.random();
    const value = 0.8 + 0.2 * Math.random();
    return HSVtoRGB(hue, saturation, value);
}

type ColorObject = {
    r: number;
    g: number;
    b: number;
    a: number;
};

/**
 * {@link ColorMask} is a value consisting of 3 color components; red, green and blue.\
 * The class extends from {@link vec3} and offers convenience methods for colors.
 */
export class ColorMask {
    public value: vec3;

    /**
     * Generates a randomized color that is not gray scale or too dark to be seen
     */
    public constructor();

    /**
     * Creates a copy of another {@link ColorMask color}.
     * @param ref The {@link ColorMask color} to be copied.
     */
    public constructor(ref: vec3);

    /**
     * Generates {@link ColorMask color} from 3 color components.
     * @param red The red color-component.
     * @param green The green color-component.
     * @param blue The blue color-component.
     */
    public constructor(red: number, green: number, blue: number);

    /**
     * Generates {@link ColorMask color} from {@link ColorObject}.
     * @param color The color object that is to be converted into {@link ColorMask}.
     */
    public constructor(color: ColorObject);

    /**
     * Generates {@link ColorMask color} from an array of numbers.
     * @param array The array defining the color component values.
     */
    public constructor(array: number[]);

    public constructor(
        red?: number | vec3 | ColorObject | number[],
        green?: number,
        blue?: number
    ) {
        if (red instanceof glMatrix.ARRAY_TYPE || red instanceof Float32Array) {
            this.value = vec3.clone(red as vec3);
        } else if (Array.isArray(red)) {
            this.value = vec3.fromValues(red[0], red[1], red[2]);
        } else if (red === undefined) {
            this.value = randomColor();
        } else if (red instanceof Object) {
            this.value = vec3.fromValues(red.r, red.g, red.b);
        } else {
            this.value = vec3.fromValues(red, green as number, blue as number);
        }
    }

    /**
     * Method to get GRAY_SCALE {@link ColorMask}, which has all color components as 1.
     */
    public static get GRAY_SCALE(): ColorMask {
        return new ColorMask(1, 1, 1);
    }

    /**
     * Method to get RED {@link ColorMask}, which has value (1, 0, 0).
     */
    public static get RED(): ColorMask {
        return new ColorMask(1, 0, 0);
    }

    /**
     * Method to get GREEN {@link ColorMask}, which has value (0, 1, 0).
     */
    public static get GREEN(): ColorMask {
        return new ColorMask(0, 1, 0);
    }

    /**
     * Method to get BLUE {@link ColorMask}, which has value (0, 0, 1).
     */
    public static get BLUE(): ColorMask {
        return new ColorMask(0, 0, 1);
    }

    /**
     * Setter for the {@link vec3.x}-component.
     */
    public set red(red: number) {
        this.value[0] = red;
    }

    /**
     * Getter for the {@link vec3.x}-component.
     */
    public get red(): number {
        return this.value[0];
    }

    /**
     * Setter for the {@link vec3.y}-component.
     */
    public set green(green: number) {
        this.value[1] = green;
    }

    /**
     * Getter for the {@link vec3.y}-component.
     */
    public get green(): number {
        return this.value[1];
    }

    /**
     * Setter for the {@link vec3.z}-component.
     */
    public set blue(blue: number) {
        this.value[2] = blue;
    }

    /**
     * Getter for the {@link vec3.z}-component.
     */
    public get blue(): number {
        return this.value[2];
    }
}
